import { z } from 'zod'

// v4's own reqparse choices (InsightsOptions._member_names_, confirmed against
// app/model/support/insights_breakdown_options.py) are 'ads'/'adsets' —
// identical to the values InsightsBreakdown.jsx already sends today. The
// confluence guide's AD/ADSET is stale; no value mapping is needed between
// v3 and v4 here (see api.js's fetchInsightsBreakdownAsync).
export const BreakdownBy = Object.freeze({
  ads: 'ads',
  adsets: 'adsets',
})

export const EventEvaluationErrorCode = Object.freeze({
  marketingTooShortToEvaluate: 'error.event_evaluation.marketing_too_short_to_evaluate',
  evaluationUnavailableYet: 'error.event_evaluation.evaluation_unavailable_yet',
})

// GET /insights — user_action_required is null for platform=google (no Meta
// campaigns to check against). reach for Google is the true campaign-level
// unique-users figure from the Google Ads API — do not sum/re-aggregate it
// client-side the way per-day Meta rows might be summed elsewhere.
export const eventInsightsResponseSchema = z
  .object({
    ad_spend: z.number().nullable().optional(),
    reach: z.number().nullable().optional(),
    impressions: z.number().nullable().optional(),
    clicks: z.number().nullable().optional(),
    ctr: z.number().nullable().optional(),
    start: z.string().nullable().optional(),
    end: z.string().nullable().optional(),
    user_action_required: z.boolean().nullable().optional(),
  })
  .passthrough()

const singleCampaignResultSchema = z
  .object({
    total_transactions: z.number().nullable().optional(),
    total_revenue: z.number().nullable().optional(),
    roas: z.number().nullable().optional(),
    cpo: z.number().nullable().optional(),
  })
  .passthrough()

const platformMetaAttributionSchema = z
  .object({
    eval_name: z.string().nullable().optional(),
    ad_account_id: z.string().nullable().optional(),
  })
  .passthrough()

const platformGoogleAttributionSchema = z
  .object({
    eval_name: z.string().nullable().optional(),
    customer_id: z.string().nullable().optional(),
  })
  .passthrough()

// Only carries keys for the requested platform(s) — a ?platform=google
// request has no `meta` key at all (not an empty one). Also null wholesale
// when no attribution configuration is found at all (e.g. no published
// campaign setup with conversion details).
const platformAttributionSchema = z
  .object({
    meta: platformMetaAttributionSchema.optional(),
    google: platformGoogleAttributionSchema.optional(),
  })
  .passthrough()
  .nullable()

// GET /campaign_results — series_results is non-null only for series events
// under FD-live attribution (not platform-native).
export const campaignResultsResponseSchema = z
  .object({
    event_results: singleCampaignResultSchema,
    series_results: singleCampaignResultSchema.nullable().optional(),
    platform_attribution: platformAttributionSchema.optional(),
  })
  .passthrough()

export const insightsBreakdownItemSchema = z
  .object({
    id: z.string().nullable().optional(),
    platform: z.enum(['meta', 'google']).nullable().optional(),
    name: z.string(),
    status: z.string().nullable().optional(),
    spend: z.string().nullable().optional(),
    impressions: z.string().nullable().optional(),
    clicks: z.string().nullable().optional(),
    reach: z.string().nullable().optional(),
    ctr: z.string().nullable().optional(),
    purchase: z.string().nullable().optional(),
    revenue: z.string().nullable().optional(),
    roi: z.string().nullable().optional(),
    cpo: z.string().nullable().optional(),
    rank: z.string().nullable().optional(),
  })
  .passthrough()

export const insightsBreakdownResponseSchema = z
  .object({
    total: z.number(),
    limit: z.number(),
    page: z.number(),
    pages: z.number(),
    nextPage: z.string().nullable().optional(),
    prevPage: z.string().nullable().optional(),
    items: z.array(insightsBreakdownItemSchema),
  })
  .passthrough()

// GET /insights/top-performing-ads — Meta and Google items have genuinely
// different shapes (Meta: tc/body/call_to_action, no ad_score; Google:
// ad_score/description/long_headline, no tc/body/call_to_action). Don't
// unify into one schema.
export const metaTopAdItemSchema = z
  .object({
    id: z.string(),
    name: z.string(),
    tc: z.string().nullable().optional(),
    headline: z.string().nullable().optional(),
    body: z.string().nullable().optional(),
    media: z.any().nullable().optional(),
    destination_url: z.string().nullable().optional(),
    call_to_action: z.string().nullable().optional(),
  })
  .passthrough()

export const googleTopAdItemSchema = z
  .object({
    id: z.string(),
    name: z.string(),
    // null for ads that have never had an insight import.
    ad_score: z.number().nullable().optional(),
    ad_type: z.string().nullable().optional(),
    headline: z.string().nullable().optional(),
    description: z.string().nullable().optional(),
    long_headline: z.string().nullable().optional(),
    // A flat media item, or a Display/Demand Gen bundle of several ratios.
    media: z.any().nullable().optional(),
    destination_url: z.string().nullable().optional(),
    call_to_action: z.string().nullable().optional(),
    business_name: z.string().nullable().optional(),
    logo: z.any().nullable().optional(),
    display_url_path: z.any().nullable().optional(),
    companion_banner: z.any().nullable().optional(),
  })
  .passthrough()

// Only the requested platform keys appear — a ?platform=google request omits
// `meta` entirely, not an empty array.
export const topPerformingAdsResponseSchema = z
  .object({
    meta: z.array(metaTopAdItemSchema).optional(),
    google: z.array(googleTopAdItemSchema).optional(),
  })
  .passthrough()
